import React from 'react';
import { TABS_BLOCK } from '@eeacms/volto-tabs-block/constants';
import {
  audioFileValidator,
  blockImageAltTextValidator,
  javaScriptFileValidator,
  missingLeadImageAltTextValidator,
  videoFileValidator,
} from 'govbr.frontend.policy/helpers/validations';
import uniqBy from 'lodash/uniqBy';
import { defineMessages } from 'react-intl';

import { GTM, WebStatsRenderer } from './components';
import AuditLog from './components/AuditLog/AuditLog';
import AuditSite from './components/AuditSite/AuditSite';
import applyConfigBlockVariations from './components/BlockTemplates';
import { HTMLDomainsControlpanel } from './components/Controlpanels';
import ToolbarUserMenuPlug from './components/Plugs/ToolbarUserMenuPlug';
import ProfileReport from './components/ProfileReport/ProfileReport';
import SearchUf from './components/SearchUf/SearchUf';
import TextAlignButton from './components/Slate/TextAlignButton';
import { unwantedControlPanelsFields } from './controlpanels-config';
import { validateAccordionBlock } from './customizations/@eeacms/volto-accordion-block/components/manage/Blocks/Accordion/AccordionValidator';
import { validateColumnsBlock } from './customizations/@eeacms/volto-columns-block/ColumnsBlock/ColumnBlockValidator';
import { validateTabsBlock } from './customizations/@eeacms/volto-tabs-block/components/TabsValidator';
import TableBlockSchema from './customizations/@plone/volto-slate/blocks/Table/schema';
import { ImageSchema } from './customizations/volto/components/manage/Blocks/Image/schema';
import { objectListWidgetValidator } from './customizations/volto/components/manage/Widgets/ObjectListWidgetValidator';
import { objectWidgetValidator } from './customizations/volto/components/manage/Widgets/ObjectWidgetValidator';
import withRequiredRoles from './helpers/Guards/withRequiredRoles';
import * as addonReducers from './reducers';
import registerSlotComponents from './slotComponents';
import {
  AudioFileWidget,
  JavaScriptFileWidget,
  PositionWidget,
  SocialNetworksWidget,
  TextAlignWidget,
  VideoFileWidget,
} from './widgets';
import ArrayListWidget from './widgets/ArrayListWidget';
import HelpMessageWidget from './widgets/HelpMessageWidget';
import SiorgWidget from './widgets/SiorgWidget/SiorgWidget';

import alignCenterIcon from '@plone/volto/icons/align-center.svg';
import alignLeftIcon from '@plone/volto/icons/align-left.svg';
import alignRightIcon from '@plone/volto/icons/align-right.svg';
import tabsSVG from 'govbr.frontend.policy/icons/tabs.svg';

const slateAlignMessages = defineMessages({
  alignLeft: {
    id: 'Align left',
    defaultMessage: 'Align left',
  },
  alignCenter: {
    id: 'Align center',
    defaultMessage: 'Align center',
  },
  alignRight: {
    id: 'Align right',
    defaultMessage: 'Align right',
  },
});

defineMessages({
  Organization: {
    id: 'organization',
    defaultMessage: 'Organization',
  },
  Advanced: {
    id: 'advanced',
    defaultMessage: 'Advanced',
  },
  contentTitle: {
    id: 'Content title',
    defaultMessage: 'Content title',
  },
  contentDescription: {
    id: 'Content description',
    defaultMessage: 'Content description',
  },
  tabs: {
    id: 'Tabs',
    defaultMessage: 'Tabs',
  },
  withImage: {
    id: 'With image',
    defaultMessage: 'With image',
  },
});

const hideBlocks = ['hero', 'toc', 'gridBlock'];

/*
  Esta função deve ser chamada por úlimo em index.js.
  Algumas configurações não podem ser aplicadas antes no index.js do
  govbr.frontend.policy,porquer alguns addons não foram carregados ainda, quando
  o govbr.frontend.policy é carregado. Então para configurar coisas que dependem
  de outros addons, precisamos fazer isso por útimo.
*/

const BLOCKED_EXPANDERS = ['navigation'];

const applyConfigAddon = (config) => {
  hideBlocks.forEach((block) => delete config.blocks.blocksConfig[block]);

  // Remove navigation e navroot dos expanders globais pois não é necessário
  // recalcular essas estruturas em toda chamada.
  // Isso reduz drasticamente o tempo de resposta e o payload em sites grandes.

  config.settings.apiExpanders = config.settings.apiExpanders.map(
    (expander) => {
      if (!expander.GET_CONTENT) return expander;

      const cleaned = {
        ...expander,
        GET_CONTENT: expander.GET_CONTENT.filter(
          (item) => !BLOCKED_EXPANDERS.includes(item),
        ),
      };

      // Remove qualquer querystring associada a esses expanders
      if (cleaned.querystring) {
        delete cleaned.querystring;
      }

      return cleaned;
    },
  );

  // Adiciona reducers do pacote.
  config.addonReducers = {
    ...config.addonReducers,
    ...addonReducers,
  };

  config.settings = {
    ...config.settings,

    // Adiciona /report as rotas que não são de conteúdo (não exibe breadcrumb)
    nonContentRoutes: [
      ...(config.settings.nonContentRoutes || []),
      /\/log\/.*$/,
    ],

    // Define as configuracoes de multi idioma do site
    isMultilingual: true,
    supportedLanguages: ['pt-br', 'en', 'es'],
    defaultLanguage: 'pt-br',

    // Altera a profundidade do menu
    navDepth: 3,
    // footerNavDepth: 2,

    showRelatedItems: true,

    // desabilita a exibição padrão das palavras-chave
    // que foi substituida pelo componente
    showTags: false,

    defaultPageSize: 24,

    unwantedControlPanelsFields: unwantedControlPanelsFields,

    hasWorkingCopySupport: true,
    disableA11yChecker: false,

    // Links de objetos do tipo arquivo direcionam para a view mesmo para anônimos
    downloadableObjects: [],

    appExtras: [
      ...(config.settings.appExtras ?? []),
      {
        match: '',
        component: GTM,
      },
      {
        match: '',
        component: WebStatsRenderer,
      },
      {
        match: '',
        component: ToolbarUserMenuPlug,
      },
    ],

    serverMetricsEnabled: true,

    querystringSearchGet: true,
  };

  // Adiciona rota para relatório de auditoria no control panel
  config.addonRoutes = [
    ...(config.addonRoutes || []),
    {
      path: '/log/audit',
      component: withRequiredRoles(AuditLog, ['Manager', 'Site Administrator']),
    },
    {
      // Registra ambos os caminhos para que `loadOnServer` (que usa a URL completa,
      // incluindo o prefixo `RAZZLE_PREFIX_PATH`) consiga casar esta rota e
      // despache os itens assíncronos (ex.: getUser) antes da renderização.
      path: config.settings.prefixPath
        ? ['/log/audit-site', `${config.settings.prefixPath}/log/audit-site`]
        : '/log/audit-site',
      component: withRequiredRoles(AuditSite, ['Manager', 'Parameter Manager']),
    },
    {
      path: '/log/profiles',
      component: withRequiredRoles(ProfileReport, [
        'Manager',
        'Site Administrator',
      ]),
    },
    {
      path: [
        `/(${config.settings?.supportedLanguages.join('|')})/listagem_noticias_por_uf`,
        `/(${config.settings?.supportedLanguages.join('|')})/**/listagem_noticias_por_uf`,
      ],
      component: SearchUf,
    },
    {
      path: '/controlpanel/html-domains-properties',
      component: (props) => {
        return (
          <HTMLDomainsControlpanel
            {...props}
            id="html-domains-properties"
            pathname={props.location?.pathname}
          />
        );
      },
    },
  ];

  // Remove links automáticos em headings no bloco de texto
  config.settings.slate = {
    ...(config.settings.slate || {}),
    useLinkedHeadings: false,
  };

  config.settings.slate.buttons = {
    ...(config.settings.slate.buttons || {}),
    alignLeft: () => (
      <TextAlignButton
        alignment="left"
        icon={alignLeftIcon}
        title={slateAlignMessages.alignLeft}
      />
    ),
    alignCenter: () => (
      <TextAlignButton
        alignment="center"
        icon={alignCenterIcon}
        title={slateAlignMessages.alignCenter}
      />
    ),
    alignRight: () => (
      <TextAlignButton
        alignment="right"
        icon={alignRightIcon}
        title={slateAlignMessages.alignRight}
      />
    ),
  };

  config.settings.slate.toolbarButtons = [
    ...(config.settings.slate.toolbarButtons || []),
    'separator',
    'alignLeft',
    'alignCenter',
    'alignRight',
  ];
  config.settings.slate.expandedToolbarButtons = [
    ...(config.settings.slate.expandedToolbarButtons || []),
    'separator',
    'alignLeft',
    'alignCenter',
    'alignRight',
  ];

  config.settings.appExtras = (config.settings.appExtras || []).filter(
    (extra) => extra?.component?.name !== 'ScrollToAnchor',
  );

  config.registerUtility({
    type: 'validator',
    name: 'altText',
    dependencies: {
      behaviorName: 'govbr.leadimage',
      fieldName: 'alt',
    },
    method: missingLeadImageAltTextValidator,
  });

  // Registra validadores de blocos
  config.registerUtility({
    type: 'validate_block',
    name: 'validate_columns_block',
    dependencies: {
      blockType: 'columnsBlock',
    },
    method: validateColumnsBlock,
  });

  config.registerUtility({
    type: 'validate_block',
    name: 'validate_accordion_block',
    dependencies: {
      blockType: 'accordion',
    },
    method: validateAccordionBlock,
  });

  config.registerUtility({
    type: 'validate_block',
    name: 'validate_tabs_block',
    dependencies: {
      blockType: 'tabs_block',
    },
    method: validateTabsBlock,
  });

  config.registerUtility({
    type: 'validator',
    name: 'altText_teaser',
    dependencies: {
      blockType: 'teaser',
      fieldName: 'image_alt',
    },
    method: blockImageAltTextValidator,
  });

  config.registerUtility({
    name: 'object_list_validator_iternal_items',
    type: 'validator',
    dependencies: { widget: 'object_list' },
    method: objectListWidgetValidator,
  });

  config.registerUtility({
    name: 'object_validator_iternal_items',
    type: 'validator',
    dependencies: { widget: 'object' },
    method: objectWidgetValidator,
  });

  config.registerUtility({
    type: 'validator',
    name: 'validate_audio_file',
    dependencies: {
      behaviorName: 'govbr.audio_file',
      fieldName: 'audio_file',
    },
    method: audioFileValidator,
  });

  config.registerUtility({
    type: 'validator',
    name: 'validate_video_file',
    dependencies: {
      widget: 'video_file_widget',
    },
    method: videoFileValidator,
  });

  config.registerUtility({
    type: 'validator',
    name: 'validate_javascript_file',
    dependencies: {
      widget: 'javascript_file_widget',
    },
    method: javaScriptFileValidator,
  });

  config.blocks = {
    ...config.blocks,

    // Remove obrigatoriedade do bloco de título
    requiredBlocks: [],

    // Adiciona grupos Metadados e Gov.br
    // Altera ordem dos grupos
    // Para traduzir, o id do bloco deve corresponder ao id da mensagem
    groupBlocksOrder: uniqBy(
      [
        { id: 'mostUsed', title: 'Most used' },
        { id: 'text', title: 'Text' },
        { id: 'layout', title: 'Layout' },
        { id: 'listing', title: 'Listing' },
        { id: 'media', title: 'Media' },
        { id: 'govbr', title: 'Gov.br' },
        { id: 'organization', title: 'Organization' },
        { id: 'advanced', title: 'Advanced' },
        ...config.blocks.groupBlocksOrder,
      ],
      'id',
    ),
  };

  // Adiciona variações customizadas de blocos
  applyConfigBlockVariations(config);

  // Adiciona variações ao bloco de listagem
  // config.blocks.blocksConfig.listing.variations =
  //   config.blocks.blocksConfig.listing.variations.map((variation) => {
  //     if (variation.id === 'summary')
  //       return {
  //         ...variation,
  //         title: 'With image',
  //       };
  //     return variation;
  //   });

  // config.blocks.blocksConfig.listing.variations =
  //   config.blocks.blocksConfig.listing.variations.filter(
  //     (variation) => variation.id !== 'imageGallery',
  //   );

  // Adiciona o blocos de colunas e destaque ao grupo Mais Usados
  config.blocks.blocksConfig.columnsBlock.mostUsed = true;
  config.blocks.blocksConfig.teaser.mostUsed = true;

  // Customiza ícone, grupo e título do bloco da abas.
  config.blocks.blocksConfig[TABS_BLOCK].icon = tabsSVG;
  config.blocks.blocksConfig[TABS_BLOCK].group = 'layout';
  config.blocks.blocksConfig[TABS_BLOCK].title = 'Tabs';
  config.blocks.blocksConfig[TABS_BLOCK].variations = [];

  // Move blocos padrão do Volto para os grupos definidos para o Gov
  config.blocks.blocksConfig.slate.group = 'text';
  config.blocks.blocksConfig.title.group = 'text';
  config.blocks.blocksConfig.description.group = 'text';
  config.blocks.blocksConfig.listing.group = 'listing';
  config.blocks.blocksConfig.columnsBlock.group = 'layout';
  config.blocks.blocksConfig.teaser.group = 'layout';
  config.blocks.blocksConfig.accordion.group = 'layout';
  config.blocks.blocksConfig.slateTable.group = 'layout';
  config.blocks.blocksConfig.html.group = 'advanced';

  if (config.blocks.blocksConfig.repeatableContentBlock) {
    config.blocks.blocksConfig.repeatableContentBlock.group = 'advanced';
    config.blocks.blocksConfig.repeatableContentBlock.mostUsed = false;
  }

  config.blocks.blocksConfig.form.group = 'advanced';
  config.blocks.blocksConfig.form.mostUsed = false;

  // Remove os campos default_from (remetente) e remove_data_after_days
  // (exclusão dos dados) da sidebar do bloco de formulário.
  const originalFormSchema = config.blocks.blocksConfig.form.formSchema;
  config.blocks.blocksConfig.form.formSchema = (data) => {
    const schema = originalFormSchema(data);
    const hiddenFields = ['default_from', 'remove_data_after_days'];
    schema.fieldsets = schema.fieldsets.map((fieldset) => ({
      ...fieldset,
      fields: fieldset.fields.filter((f) => !hiddenFields.includes(f)),
    }));
    schema.required = (schema.required || []).filter(
      (f) => !hiddenFields.includes(f),
    );
    return schema;
  };

  // Mantem o bloco 'search' (apenas restringido) para preservar suas extensões
  // (facetWidgets) usadas pelo bloco customizado 'customSearch'. Deletar o bloco
  // quebraria o schema (TypeError ao acessar extensions).
  config.blocks.blocksConfig.search.restricted = true;

  // Altera nomes dos blocos do conteúdo
  config.blocks.blocksConfig.title.title = 'Content title';
  config.blocks.blocksConfig.description.title = 'Content description';

  // Customiza grupo Mais usados para blocos padrão do Volto
  config.blocks.blocksConfig.slate.mostUsed = true;
  config.blocks.blocksConfig.video.mostUsed = false;

  config.blocks.blocksConfig.maps.group = 'media';

  config.blocks.blocksConfig.accordion.blockHasOwnFocusManagement = true;

  // Habilita novo botão de adicionar blocos
  // config.experimental.addBlockButton.enabled = true;

  // config.blocks.blocksConfig.accordion.schemaEnhancer = () => {return {}};
  config.blocks.blocksConfig.accordion.schemaEnhancer = null;

  // Registra Widgets
  config.widgets.widget.position_widget = PositionWidget;
  config.widgets.widget.social_networks_widget = SocialNetworksWidget;
  config.widgets.widget.siorg_widget = SiorgWidget;
  config.widgets.widget.array_list = ArrayListWidget;
  config.widgets.widget.help_message_widget = HelpMessageWidget;
  config.widgets.widget.text_align_widget = TextAlignWidget;
  config.widgets.widget.audio_file_widget = AudioFileWidget;
  config.widgets.widget.video_file_widget = VideoFileWidget;
  config.widgets.widget.javascript_file_widget = JavaScriptFileWidget;

  // Adiciona script nas páginas de erro.
  // FIXME: https://github.com/plone/volto/issues/6048
  config.settings.serverConfig.extractScripts = { errorPages: true };

  // Remove o "Advanced settings" do bloco de texto.
  // TODO: remover quando o TOC (table of contents) for habilitado.
  config.blocks.blocksConfig.slate.tocEntry = false;

  // Para funcionar validação do bloco de imagem
  // TODO: corrigir no volto
  config.blocks.blocksConfig.image = {
    ...config.blocks.blocksConfig.image,
    blockSchema: ImageSchema,
  };

  // Para funcionar validacao do titulo obrigatorio no bloco de tabela
  config.blocks.blocksConfig.slateTable = {
    ...config.blocks.blocksConfig.slateTable,
    blockSchema: TableBlockSchema,
  };

  // Descricao so um por pagina
  // TODO: Sugerir no volto
  config.blocks.blocksConfig.description.restricted = ({ properties, block }) =>
    properties.blocks_layout?.items?.find(
      (uid) => properties.blocks?.[uid]?.['@type'] === block.id,
    );

  // Altera LeadImage para aparecer somente uma vez por página
  // TODO: Sugerir no volto
  config.blocks.blocksConfig.leadimage.restricted = ({ properties, block }) =>
    !properties.hasOwnProperty('image') ||
    properties.blocks_layout?.items?.find(
      (uid) => properties.blocks?.[uid]?.['@type'] === block.id,
    );

  // INFO: a validacao de bloco de ordem de blocos depende desta configuração
  // ATENÇÃO: os blocos iniciais do News Item devem estar sincronizados na migração em:
  // https://gitcorporativo.serpro/sgc/govbr-volto-12677/volto.transmogrifier/-/blob/main/src/volto/transmogrifier/sections/initialize_blocks.py

  config.settings.initialBlocksOrderValidatedContentTypes = [
    'News Item',
    'Event',
    'Video',
  ];
  config.blocks.initialBlocks = {
    ...config.blocks.initialBlocks,
    'News Item': [
      { '@type': 'subtitleBlock', fixed: true },
      { '@type': 'title', fixed: true, required: true },
      { '@type': 'description', fixed: true },
      { '@type': 'dateSocialShareBlock', fixed: true, required: true },
      { '@type': 'textToSpeech', fixed: true, required: true },
      { '@type': 'leadimage', fixed: true },
      { '@type': 'slate' },
    ],
    Event: [
      { '@type': 'title', fixed: true, required: true },
      { '@type': 'dateSocialShareBlock', fixed: true, required: true },
      { '@type': 'leadimage', fixed: true },
      { '@type': 'description', fixed: true },
      { '@type': 'eventInfoBlock', fixed: true, required: true },
    ],
    Video: [
      { '@type': 'title', fixed: true, required: true },
      { '@type': 'description', fixed: true },
      { '@type': 'dateSocialShareBlock', fixed: true, required: true },
      { '@type': 'videoContent', fixed: true, required: true },
    ],
  };

  registerSlotComponents(config);

  config.settings.viewableInBrowserObjects = ['File'];

  return config;
};

export default applyConfigAddon;
